/**
 * OtaSyncDialog — modal for "Verifica e sincronizza" prices from Booking/Airbnb statements.
 *
 * Flow:
 *   1) User picks a CSV/XLS/XLSX file exported from Booking Extranet or Airbnb host.
 *   2) Backend parses the file AND matches each row against existing bookings in the DB,
 *      returning candidate bookings with a confidence score.
 *   3) For each row, the user can:
 *        - accept the auto-pick (green check, ≥70 confidence),
 *        - choose a candidate from a dropdown (ambiguous: ≥2 candidates),
 *        - manually map to a booking by code (unmatched),
 *        - or skip the row entirely.
 *   4) On "Applica correzioni" → POST /admin/bookings/sync-ota/apply.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Upload, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet, RefreshCcw, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";

const STATUS_STYLES = {
  matched: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", icon: CheckCircle2, label: "Match certo" },
  ambiguous: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: AlertTriangle, label: "Ambiguo · scegli" },
  unmatched: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: XCircle, label: "Nessun match" },
};

export default function OtaSyncDialog({ open, onOpenChange, onApplied }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [parsed, setParsed] = useState(null); // { source, period, rows, match_summary, totals }
  // rowState: { [reservationCode_or_idx]: { selectedBookingId, fields: [...], skip: bool } }
  const [rowState, setRowState] = useState({});

  const reset = () => {
    setFile(null); setParsed(null); setRowState({});
  };

  const analyze = async () => {
    if (!file) {
      toast.error("Seleziona prima un file");
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/bookings/sync-ota/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      // Seed rowState: matched rows are pre-selected with auto-picked booking,
      // ambiguous/unmatched are seeded with no selection.
      const seed = {};
      (data.rows || []).forEach((r, i) => {
        const key = r.reservation_code || `idx_${i}`;
        seed[key] = {
          selectedBookingId: r.selected_booking_id || "",
          // By default we update everything we have (Q2 user choice "c. Tutto")
          fields: ["total", "commission", "city_tax", "withholding", "payment_fee", "net"],
          skip: r.status === "CANCELLED" || (!r.selected_booking_id && r.match_status === "unmatched"),
        };
      });
      setRowState(seed);
      toast.success(`File analizzato: ${data.source} · ${data.match_summary?.matched || 0} match certi su ${data.rows.length} righe`);
    } catch (e) {
      toast.error(errMsg(e, "Errore analisi file"));
    } finally {
      setLoading(false);
    }
  };

  const createMissingBooking = async (rowIndex) => {
    const r = parsed?.rows?.[rowIndex];
    if (!r) return;
    if (!r.check_in || !r.check_out || !r.guest_name) {
      toast.error("Riga incompleta: serve nome ospite + date");
      return;
    }
    try {
      const { data } = await api.post("/admin/bookings/sync-ota/create-from-row", {
        guest_name: r.guest_name,
        check_in: r.check_in,
        check_out: r.check_out,
        gross: r.gross,
        reservation_code: r.reservation_code,
        source: parsed?.source || "ota",
      });
      toast.success(`Prenotazione creata: ${data.code}`);
      // Inject the new candidate into the parsed row's candidate list and select it
      setParsed((p) => {
        if (!p) return p;
        const rows = p.rows.map((rr, i) => {
          if (i !== rowIndex) return rr;
          const newCand = {
            id: data.id, code: data.code, guest_name: data.guest_name,
            check_in: data.check_in, check_out: data.check_out,
            current_total: data.current_total, score: 100, reasons: ["created_now"],
          };
          return {
            ...rr,
            match_status: "matched",
            match_candidates: [newCand, ...(rr.match_candidates || [])],
            selected_booking_id: data.id,
            price_diff: 0,
          };
        });
        return { ...p, rows };
      });
      const key = r.reservation_code || `idx_${rowIndex}`;
      setRowState((p) => ({ ...p, [key]: { ...(p[key] || {}), selectedBookingId: data.id, skip: false, fields: ["total","commission","city_tax","withholding","payment_fee","net"] } }));
    } catch (e) {
      toast.error(errMsg(e, "Errore creazione prenotazione"));
    }
  };

  const applyAll = async () => {
    if (!parsed) return;
    const items = (parsed.rows || []).map((r, i) => {
      const key = r.reservation_code || `idx_${i}`;
      const st = rowState[key] || {};
      if (st.skip || !st.selectedBookingId) return null;
      return {
        booking_id: st.selectedBookingId,
        reservation_code: r.reservation_code,
        gross: r.gross, commission: r.commission, payment_fee: r.payment_fee,
        city_tax: r.city_tax, withholding: r.withholding, net: r.net,
        fields_to_update: st.fields,
      };
    }).filter(Boolean);
    if (items.length === 0) {
      toast.info("Nessuna riga da applicare");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/admin/bookings/sync-ota/apply", {
        source: parsed.source, items,
      });
      toast.success(`Sincronizzate ${data.updated} prenotazioni`);
      if (onApplied) await onApplied();
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, "Errore applicazione"));
    } finally {
      setApplying(false);
    }
  };

  const totals = useMemo(() => {
    if (!parsed) return null;
    let toApply = 0;
    let priceDiff = 0;
    (parsed.rows || []).forEach((r, i) => {
      const key = r.reservation_code || `idx_${i}`;
      const st = rowState[key] || {};
      if (!st.skip && st.selectedBookingId) {
        toApply += 1;
        if (r.price_diff != null) priceDiff += r.price_diff;
      }
    });
    return { toApply, priceDiff };
  }, [parsed, rowState]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ota-sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> Verifica e sincronizza con Booking / Airbnb
          </DialogTitle>
          <DialogDescription>
            Carica il file delle prenotazioni da Booking Extranet (es. <em>Arrivo_*.xls</em>) o Airbnb (<em>reservations.csv</em>).
            Il sistema cerca ogni prenotazione nel database e ti mostra il prezzo realmente incassato
            confrontato con quello salvato. Confermi solo quelle che vuoi correggere.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: file picker */}
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4">
          <Label className="text-xs font-semibold text-[#1E3A5F]">File OTA (CSV / XLS / XLSX)</Label>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Input
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setParsed(null); setRowState({}); }}
              data-testid="ota-sync-file-input"
              className="flex-1 min-w-[260px]"
            />
            <Button
              onClick={analyze}
              disabled={!file || loading}
              data-testid="ota-sync-analyze"
              className="bg-[#1E3A5F] hover:bg-[#15294A] text-white"
            >
              {loading ? <RefreshCcw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Analizza file
            </Button>
          </div>
          <p className="text-[11px] text-[#635F59] mt-2">
            Supportati: <code>Arrivo_*.xls</code> (Booking Extranet IT), <code>reservation_statements_overview.csv</code> (Booking EN), <code>reservations.csv</code> (Airbnb host), <code>airbnb_*.csv</code> (Airbnb full earnings)
          </p>
        </div>

        {/* Step 2: matched rows table */}
        {parsed && (
          <div className="space-y-3 mt-4" data-testid="ota-sync-results">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-[#1E3A5F] text-white font-semibold">
                {parsed.source?.toUpperCase()} · {parsed.period}
              </span>
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800">
                ✓ {parsed.match_summary?.matched || 0} match certi
              </span>
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">
                ⚠ {parsed.match_summary?.ambiguous || 0} ambigui
              </span>
              <span className="px-2 py-1 rounded bg-slate-100 text-slate-700">
                ✗ {parsed.match_summary?.unmatched || 0} senza match
              </span>
              <span className="px-2 py-1 rounded bg-blue-100 text-blue-800">
                Totale lordo: €{(parsed.totals?.gross || 0).toFixed(2)} · commissioni €{(parsed.totals?.commission || 0).toFixed(2)} · netto €{(parsed.totals?.net || 0).toFixed(2)}
              </span>
            </div>

            <div className="border border-[#E8E2D9] rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#F5F1E8] text-[#1E3A5F]">
                  <tr>
                    <th className="px-2 py-2 text-left">Stato</th>
                    <th className="px-2 py-2 text-left">Ospite (OTA)</th>
                    <th className="px-2 py-2 text-left">Date</th>
                    <th className="px-2 py-2 text-right">€ OTA</th>
                    <th className="px-2 py-2 text-left">Prenotazione abbinata</th>
                    <th className="px-2 py-2 text-right">€ Sistema</th>
                    <th className="px-2 py-2 text-right">Diff</th>
                    <th className="px-2 py-2 text-center">Applica</th>
                  </tr>
                </thead>
                <tbody>
                  {(parsed.rows || []).map((r, i) => {
                    const key = r.reservation_code || `idx_${i}`;
                    const st = rowState[key] || {};
                    const styles = STATUS_STYLES[r.match_status] || STATUS_STYLES.unmatched;
                    const StatusIcon = styles.icon;
                    const isCancelled = r.status === "CANCELLED";
                    const selectedCandidate = (r.match_candidates || []).find((c) => c.id === st.selectedBookingId);
                    const diff = selectedCandidate
                      ? (Number(selectedCandidate.current_total || 0) - Number(r.gross || 0)).toFixed(2)
                      : (r.price_diff != null ? r.price_diff.toFixed(2) : "—");
                    return (
                      <tr key={key} className={`border-t border-[#E8E2D9] ${isCancelled ? "opacity-50" : ""}`} data-testid={`ota-row-${i}`}>
                        <td className={`px-2 py-2 ${styles.bg}`}>
                          <div className={`inline-flex items-center gap-1 ${styles.text} font-semibold`}>
                            <StatusIcon className="w-3 h-3" />
                            <span className="text-[10px] uppercase">{isCancelled ? "Cancellata" : styles.label}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="font-semibold">{r.guest_name || "—"}</div>
                          <div className="text-[10px] text-[#635F59] font-mono">{r.reservation_code}</div>
                        </td>
                        <td className="px-2 py-2 text-[10px]">
                          {r.check_in} → {r.check_out}
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-semibold text-[#1E3A5F]">
                          €{Number(r.gross || 0).toFixed(2)}
                          {r.commission > 0 && (
                            <div className="text-[9px] text-rose-700">comm. −€{Number(r.commission).toFixed(2)}</div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {r.match_candidates?.length > 0 ? (
                            <Select
                              value={st.selectedBookingId || ""}
                              onValueChange={(v) => setRowState((p) => ({ ...p, [key]: { ...p[key], selectedBookingId: v, skip: false } }))}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`ota-row-${i}-select`}>
                                <SelectValue placeholder="Scegli prenotazione…" />
                              </SelectTrigger>
                              <SelectContent>
                                {r.match_candidates.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.code} · {c.guest_name} · {c.check_in}
                                    {c.guide_room_number != null && ` · #${c.guide_room_number}`}
                                    {" · score "}{c.score}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-[10px] text-slate-500 italic">nessun candidato nel sistema</span>
                          )}
                          {/* "+ Crea prenotazione" link — visible only when no booking has been
                              picked yet AND the OTA row is not cancelled. Lets the admin import
                              a guest that's in Booking/Airbnb but NOT yet in the system. */}
                          {!isCancelled && !st.selectedBookingId && r.guest_name && r.check_in && r.check_out && (
                            <button
                              type="button"
                              onClick={() => createMissingBooking(i)}
                              className="text-[10px] text-[#AC5D45] hover:text-[#94472f] hover:underline mt-1 font-semibold inline-flex items-center gap-0.5"
                              data-testid={`ota-row-${i}-create`}
                            >
                              <Plus className="w-3 h-3" /> Crea nuova prenotazione da questa riga OTA
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right font-mono">
                          {selectedCandidate ? `€${Number(selectedCandidate.current_total || 0).toFixed(2)}` : "—"}
                        </td>
                        <td className={`px-2 py-2 text-right font-mono font-bold ${
                          selectedCandidate && Number(diff) !== 0
                            ? Number(diff) > 0 ? "text-rose-700" : "text-emerald-700"
                            : "text-slate-400"
                        }`}>
                          {selectedCandidate && Number(diff) !== 0 ? `${Number(diff) > 0 ? "−" : "+"}€${Math.abs(Number(diff)).toFixed(2)}` : "—"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <Checkbox
                            checked={!st.skip && !!st.selectedBookingId}
                            disabled={!st.selectedBookingId || isCancelled}
                            onCheckedChange={(v) => setRowState((p) => ({ ...p, [key]: { ...p[key], skip: !v } }))}
                            data-testid={`ota-row-${i}-apply`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Step 3: summary + apply */}
            {totals && (
              <div className="bg-[#1E3A5F] text-white rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  Verranno aggiornate <strong>{totals.toApply}</strong> prenotazioni
                  {totals.priceDiff !== 0 && (
                    <span className="ml-3 text-amber-200">
                      Scostamento totale prezzo: <strong>€{Math.abs(totals.priceDiff).toFixed(2)}</strong>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="ota-sync-cancel">
            Annulla
          </Button>
          {parsed && (
            <Button
              onClick={applyAll}
              disabled={applying || !totals || totals.toApply === 0}
              className="bg-[#AC5D45] hover:bg-[#94472f] text-white"
              data-testid="ota-sync-apply"
            >
              {applying ? <RefreshCcw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Applica correzioni ({totals?.toApply || 0})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
